import { petCheckMode, petCheckBonuses, type PetEquipment } from './petEquipmentService.ts';
import type { ContentRegistry } from '../content/registry.ts';
import type { StatId } from '../domain/stats.ts';
import { STAT_LABELS } from '../domain/stats.ts';
import type { PetCompanion } from '../domain/pet.ts';
import { petFloorRank } from '../domain/pet.ts';
import type { Roller } from '../rules/dice.ts';
import type { RollMode } from '../rules/rollModes.ts';
import { resolveAttackCheck, type AttackCheckOutcome } from '../rules/attackResolution.ts';
import { rollDamage, type DamageRollResult } from '../rules/damageRoll.ts';
import type { Degree } from '../rules/degrees.ts';
import { parseDice } from '../rules/damageDice.ts';
import { statModForScore } from '../rules/statModifiers.ts';

/**
 * Pet attacks are intentionally modelled separately from crawler Attacks.
 * Pets are not crawlers and their Rank is always the current Floor Number.
 * Core Rulebook pp. 230-231.
 */
export type PetAttackId = 'slice-attack' | 'back-claw' | 'bite';

export const PET_ATTACK_IDS: readonly PetAttackId[] = ['slice-attack', 'back-claw', 'bite'];

export interface PetAttackPlan {
  equipment?: PetEquipment;
  id: PetAttackId;
  name: string;
  rank: number;
  /** The pet stat block explicitly uses Str to hit for Slice Attack. */
  toHitStat: StatId;
  toHitMod: number;
  toHitFormula: string;
  damageStat: StatId | null;
  damageStatMod: number | null;
  /** Base damage after the pet-only Floor 5/10/15 extra-die scaling. */
  baseDamageDice: string;
  damageType: string;
  aggressiveBonus: number;
  damageFormula: string;
  notes: string[];
}

function signed(value: number): string {
  return value >= 0 ? `+${value}` : String(value);
}

function assertFloor(floor: number): void {
  if (!Number.isInteger(floor) || floor < 1) {
    throw new Error(`Pet Floor must be a whole number of at least 1, received ${floor}.`);
  }
}

/** One additional BASE damage die at Floors 5, 10 and 15. */
export function petBaseDamageDieCount(floor: number): number {
  assertFloor(floor);
  return 1 + (floor >= 5 ? 1 : 0) + (floor >= 10 ? 1 : 0) + (floor >= 15 ? 1 : 0);
}

export function petAttackIdsFor(pet: Pick<PetCompanion, 'role'>): PetAttackId[] {
  return pet.role === 'aggressive'
    ? ['slice-attack', 'back-claw', 'bite']
    : ['slice-attack'];
}

export function petAttackProblem(pet: Pick<PetCompanion, 'attitude' | 'healthBarSlotsRemaining'>): string | null {
  if (pet.healthBarSlotsRemaining <= 0) return 'This companion is dead and cannot attack.';
  if (pet.attitude !== 'bonded') return 'Only a Bonded pet can be directed as a companion in combat.';
  return null;
}

export function planPetAttack(
  pet: PetCompanion,
  floor: number,
  attackId: PetAttackId,
  registry: ContentRegistry,
  equipment?: PetEquipment,
): PetAttackPlan | null {
  assertFloor(floor);
  if (!petAttackIdsFor(pet).includes(attackId)) return null;

  const skill = registry.getSkill(attackId);
  const profile = registry.getAttack(attackId);
  if (!skill || !profile) return null;

  // The Core Rulebook's generic pet stat block explicitly prints Slice Attack
  // as d20 + F + Str. Back Claw and Bite are also Str-based animal attacks.
  // Do NOT inherit the animal-crawler Slice Attack Dex profile here.
  const toHitStat: StatId = 'str';
  const toHitMod = statModForScore((equipment?.enhancedStats ?? pet.unenhancedStats)[toHitStat]);
  const damageStat = profile.baseDamage.stat;
  const damageStatMod = damageStat === null ? null : statModForScore((equipment?.enhancedStats ?? pet.unenhancedStats)[damageStat]);
  const rank = Math.max(0, petFloorRank(floor) + (equipment?.modifiers.skillRankBonuses[attackId] ?? 0));

  const base = parseDice(profile.baseDamage.dice);
  // These three printed animal attacks all start with one base die. If content
  // ever changes, multiplying by the original count preserves that shape.
  const scaledCount = base.count * petBaseDamageDieCount(floor);
  const baseDamageDice = `${scaledCount}d${base.sides}`;
  const aggressiveBonus = pet.role === 'aggressive' ? floor : 0;

  const damageParts = [baseDamageDice];
  if (damageStat && damageStatMod !== null) {
    damageParts.push(`${STAT_LABELS[damageStat].abbr} Mod (${signed(damageStatMod)})`);
  }
  if (aggressiveBonus > 0) damageParts.push(`Aggressive +F (${signed(aggressiveBonus)})`);

  const notes = [...profile.limitations];
  if (floor >= 5) notes.push(`Pet Floor scaling: +1 base damage die at Floor 5${floor >= 10 ? ', 10' : ''}${floor >= 15 ? ', 15' : ''}.`);
  if (aggressiveBonus > 0) notes.push(`Aggressive role: +${floor} damage to Animal Attacks.`);

  return {
    id: attackId,
    equipment,
    name: skill.name,
    rank,
    toHitStat,
    toHitMod,
    toHitFormula: `d20 + Floor Rank (${signed(rank)}) + ${STAT_LABELS[toHitStat].abbr} Mod (${signed(toHitMod)})`,
    damageStat,
    damageStatMod,
    baseDamageDice,
    damageType: profile.baseDamage.type,
    aggressiveBonus,
    damageFormula: `${damageParts.join(' + ')} ${profile.baseDamage.type}`,
    notes,
  };
}

export function rollPetAttackCheck(
  roller: Roller,
  plan: PetAttackPlan,
  mode: RollMode,
  targetEvade: number | null = null,
): AttackCheckOutcome {
  return resolveAttackCheck(roller, {
    skillId: `pet-${plan.id}`,
    skillName: plan.name,
    toHitStat: plan.toHitStat,
    toHitStatAbbr: STAT_LABELS[plan.toHitStat].abbr,
    toHitStatMod: plan.toHitMod,
    rank: plan.rank,
    mode: petCheckMode(plan.equipment, mode, { kind: 'attack', skillId: plan.id, stat: plan.toHitStat }),
    extraComponents: petCheckBonuses(plan.equipment, { kind: 'attack', skillId: plan.id, stat: plan.toHitStat }),
  }, targetEvade);
}

export function rollPetAttackDamage(
  roller: Roller,
  plan: PetAttackPlan,
  degree: Degree,
  floor: number,
): DamageRollResult {
  assertFloor(floor);
  return rollDamage(roller, {
    // Pet scaling is already folded into this base-dice expression. This is
    // important because Critical Hits double ALL of those base dice.
    baseDice: plan.baseDamageDice,
    rank5Dice: null,
    rank: plan.rank,
    damageStat: plan.damageStat,
    damageStatAbbr: plan.damageStat ? STAT_LABELS[plan.damageStat].abbr : null,
    damageStatMod: plan.damageStatMod,
    damageType: plan.damageType,
    degree,
    floor,
    extraComponents: plan.aggressiveBonus > 0
      ? [{ label: `Aggressive role (+Floor ${floor})`, value: plan.aggressiveBonus }]
      : [],
  });
}
